import { useQuery } from '@tanstack/react-query'
import { Briefcase, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Card, CardBody } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { listCareersJobPostings } from '@/features/marketplace/careers'

/**
 * The public careers page (open job postings, no login) -- the marketplace-side half of
 * the request to connect the storefront to HR's real recruitment pipeline. Only postings
 * with `status = "open"` ever reach this list -- see `recruitment_service.list_public_job_postings`.
 */
export function StorefrontCareers() {
  const { data: postings, isLoading, error } = useQuery({
    queryKey: ['careers-job-postings'],
    queryFn: listCareersJobPostings,
    retry: false,
  })

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Careers
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Open roles across our stores, warehouse and head office.
        </p>
      </header>

      {isLoading && (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-slate-400" />
        </div>
      )}

      {error && (
        <Alert tone="error" title="Could not load job postings">
          Try again in a moment.
        </Alert>
      )}

      {postings && postings.length === 0 && (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No open roles right now. Check back soon.
            </p>
          </CardBody>
        </Card>
      )}

      {postings && postings.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {postings.map((posting) => (
            <Link key={posting.id} to={`/store/careers/${posting.id}`}>
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardBody className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Briefcase
                      className="mt-0.5 h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400"
                      aria-hidden="true"
                    />
                    <h2 className="font-medium text-slate-900 dark:text-slate-100">
                      {posting.title}
                    </h2>
                  </div>
                  {posting.department_name && (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {posting.department_name}
                    </p>
                  )}
                  {posting.location_name && (
                    <p className="flex items-center gap-1.5 text-xs text-slate-400">
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                      {posting.location_name}
                    </p>
                  )}
                  {posting.description && (
                    <p className="line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                      {posting.description}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
